// "Sign out of all devices" (Token & Session Revocation Policy — Phase A, 2026-10-06).
//
// Self-service only: the signOutAllSessions callable acts on the caller's own
// uid and needs a sign-in within the last 5 minutes, so the user re-confirms
// their password (and authenticator code if enrolled) here first. The password
// goes only to Firebase Auth. Disconnecting the user's own WhatsApp link is a
// separate, explicit, default-off choice. On success App runs the canonical
// logout (local state, caches, other tabs) via the
// 'firmassist:signed-out-all-devices' event.
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { reauthenticateWithPassword, completeReauthenticationWithTotp } from '../services/authService';

export const SIGNED_OUT_ALL_EVENT = 'firmassist:signed-out-all-devices';

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '2rem', maxWidth: '560px', marginTop: '1.5rem' };
const input = { width: '100%', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#e8eaed', fontSize: '0.95rem', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '0.75rem' };
const btn = (danger, disabled) => ({ padding: '0.7rem 1.3rem', background: disabled ? 'rgba(239,68,68,0.3)' : danger ? '#b91c1c' : 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'inherit', marginRight: '0.5rem' });

const SignOutAllDevices = ({ callSignOutAll }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState('');
  const [disconnectWhatsApp, setDisconnectWhatsApp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setOpen(false); setPassword(''); setMfa(null); setCode(''); setDisconnectWhatsApp(false); setError(''); };

  const run = async () => {
    setBusy(true); setError('');
    try {
      const re = mfa ? await completeReauthenticationWithTotp(mfa.resolver, mfa.hintUid, code) : await reauthenticateWithPassword(password);
      if (re.mfaRequired) { setMfa({ resolver: re.resolver, hintUid: re.hintUid }); setPassword(''); return; }
      if (!re.success) { setError(re.error || 'We could not confirm your identity.'); return; }
      const call = callSignOutAll || httpsCallable(getFunctions(), 'signOutAllSessions');
      await call({ disconnectWhatsApp: disconnectWhatsApp === true });
      setPassword(''); setCode('');
      window.dispatchEvent(new CustomEvent(SIGNED_OUT_ALL_EVENT));
    } catch (e) {
      const reason = e && e.details && e.details.reason;
      setError(reason === 'REAUTHENTICATION_REQUIRED' ? 'Please confirm your password again.' : 'Sign-out of all devices failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={{ color: '#e8eaed', fontSize: '1.25rem', margin: '0 0 0.25rem', fontWeight: 700 }}>Sign out of all devices</h2>
      <p style={{ color: '#8b8fa3', fontSize: '0.85rem', margin: '0 0 1rem', lineHeight: 1.6 }}>
        Ends your FirmAssist sessions everywhere, including this one. You will need to sign in again on each of your devices.
      </p>
      {!open && <button type="button" style={btn(false, false)} onClick={() => setOpen(true)}>Sign out of all devices</button>}
      {open && (
        <div role="dialog" aria-label="Confirm sign out of all devices">
          {!mfa && (
            <input type="password" aria-label="Current password" placeholder="Confirm your current password" autoComplete="current-password" style={input} value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} />
          )}
          {mfa && (
            <input aria-label="Authenticator code" placeholder="6-digit authenticator code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} style={input} value={code} disabled={busy} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
          )}
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', color: '#a8adb7', fontSize: '0.85rem', marginBottom: '1rem' }}>
            <input type="checkbox" checked={disconnectWhatsApp} disabled={busy} onChange={(e) => setDisconnectWhatsApp(e.target.checked)} />
            <span>Also disconnect my linked WhatsApp account (your firm's WhatsApp number is not affected)</span>
          </label>
          {error && <div role="alert" style={{ color: '#fca5a5', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</div>}
          <button type="button" style={btn(true, busy || (mfa ? code.length !== 6 : !password))} disabled={busy || (mfa ? code.length !== 6 : !password)} onClick={run}>
            {busy ? 'Signing out…' : 'Sign out everywhere'}
          </button>
          <button type="button" style={btn(false, busy)} disabled={busy} onClick={reset}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default SignOutAllDevices;
